// analysis → normalized ChartMeasure[] (the pure body of renderChordChart). No DOM.

import type { Analysis, ChartMeasure, ChartBeat } from "../../types/domain";
import { chordNotes } from "../theory/chords";
import { normalizeBeatLevel, applyBeatLevel } from "./beatLevel";
import { beatsToMeasures, normalizeMeasures, type RawBeat } from "./measures";

// chord with the most overlap inside [t0, t1)
export function chordAtWindow(segs: { chord: string; start: number; end: number }[], t0: number, t1: number): string | null {
  let best: string | null = null, bestOv = 0;
  for (const s of segs) {
    const ov = Math.min(s.end, t1) - Math.max(s.start, t0);
    if (ov > bestOv) { bestOv = ov; best = s.chord; }
  }
  return best;
}

export interface ChartModel {
  measures: ChartMeasure[];
  empty: boolean;   // no usable chord segments
}

export function buildChartModel(analysis: Analysis, beatLevelMul: number): ChartModel {
  const segs = (analysis.chords || []).filter((s) => chordNotes(s.chord));
  if (segs.length === 0) return { measures: [], empty: true };

  const end = analysis.duration || segs[segs.length - 1].end;
  let measures: RawBeat[][];
  if (analysis.beats && analysis.beats.length) {
    const nz = normalizeBeatLevel(analysis.beats, analysis.downbeats || [analysis.beats[0]]);
    const lv = applyBeatLevel(nz.beats, nz.downbeats, beatLevelMul);
    measures = beatsToMeasures(lv.beats, lv.downbeats, end);
    measures = normalizeMeasures(measures, lv.beats, end, lv.downbeats);
  } else {
    const beatDur = 60 / analysis.bpm, t0 = segs[0].start, bts: number[] = [];
    for (let bt = t0; bt < end - beatDur * 0.5; bt += beatDur) bts.push(bt);
    measures = [];
    for (let i = 0; i < bts.length; i += 4)
      measures.push(bts.slice(i, i + 4).map((t, j) => ({ time: t, next: bts[i + j + 1] || end })));
  }

  const chartMeasures: ChartMeasure[] = [];
  let prevChord: string | null = null;
  measures.forEach((mbeats) => {
    const beats: ChartBeat[] = mbeats.map((beat) => {
      const chord = chordAtWindow(segs, beat.time, beat.next);
      const changed = !!chord && chord !== prevChord;
      if (chord) prevChord = chord;
      return { time: beat.time, next: beat.next, chord, changed };
    });
    chartMeasures.push({
      beats,
      startTime: beats[0].time,
      endTime: mbeats[mbeats.length - 1].next || end,
    });
  });
  return { measures: chartMeasures, empty: false };
}
